import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'

export default function TrainingContentSection({
	form,
	setField,
	contentOptions = [],
}) {
	const { t } = useTranslation()
	const [localOptions, setLocalOptions] = useState([])
	const content = Array.isArray(form.content) ? form.content : []

	const mergedOptions = useMemo(() => {
		const seen = new Set()
		return [...contentOptions, ...localOptions].filter(option => {
			if (!option?.value || seen.has(option.value)) return false
			seen.add(option.value)
			return true
		})
	}, [contentOptions, localOptions])

	const addItem = () => {
		setField('content', [...content, ''])
	}

	const removeItem = index => {
		setField('content', content.filter((_, itemIndex) => itemIndex !== index))
	}

	const updateItem = (index, value) => {
		const next = [...content]
		next[index] = value
		setField('content', next)
	}

	const handleCreateOption = (index, value) => {
		const trimmed = `${value || ''}`.trim()
		if (!trimmed) return

		setLocalOptions(prev => (
			prev.some(option => option.value === trimmed)
				? prev
				: [...prev, { value: trimmed, label: trimmed }]
		))
		updateItem(index, trimmed)
	}

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<div className="flex items-center justify-between gap-3">
				<h3 className="text-sm font-semibold text-gray-900">{t('trainings.form.contentSection.title')}</h3>
				<button
					type="button"
					onClick={addItem}
					className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
				>
					{t('trainings.form.contentSection.addItem')}
				</button>
			</div>

			<div className="mt-4 space-y-3">
				{content.length
					? content.map((item, index) => {
						const selectedOption = item
							? { value: item, label: item }
							: null

						return (
							<div
								key={`training-content-${index}`}
								className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-start"
							>
								<div className="min-w-0 flex-1">
									<CreatableSelect
										options={mergedOptions}
										value={selectedOption}
										onChange={value => updateItem(index, value?.value || '')}
										onCreateOption={value => handleCreateOption(index, value)}
										placeholder={t('trainings.form.contentSection.placeholder')}
										formatCreateLabel={value => t('trainings.form.contentSection.createOption', { value })}
										noOptionsMessage={() => t('trainings.form.contentSection.noOptions')}
										className="react-select"
										classNamePrefix="react-select"
										isClearable
									/>
								</div>
								<button
									type="button"
									onClick={() => removeItem(index)}
									className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
								>
									{t('trainings.form.contentSection.removeItem')}
								</button>
							</div>
						)
					})
					: (
						<p className="text-sm text-gray-500">{t('trainings.form.contentSection.empty')}</p>
					)}
			</div>
		</div>
	)
}
